import { beginRegistrationAttempt, getPendingSegment, recordCreatedAccount } from '../features/product-segment'
import { acceptAuthenticatedDraft, discardGuestDraft, useDraftStore } from '../features/onboarding'
// useAuth — the single entry point screens use for auth actions.
//
// Centralizes what the old code scattered across auth.js: on any successful
// auth (login / register / verify-code / reset / OAuth) we normalize the
// TokenResponse into the Zustand session, fire the Metrica goal, and clear the
// invite token. Screens call these and react to the returned promise.

import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { authApi } from '../api/auth'
import { clearInviteToken, getInviteToken, getAccessToken, clearAccessToken } from '../lib/invite'
import { getYandexClientId, ensureClientIdBackfilled } from '../lib/ymUid'
import { getUserTimeZone } from '../lib/tz'
import { useOnboardingStore } from '../store/onboardingStore'
import { useMessagingStore } from '../store/messagingStore'
import { useChatwootStore } from '../store/chatwootStore'
import * as metrica from '../lib/metrica'
import type { TokenResponse } from '../types/api'
import { pushApi } from '../api/push'
import { disablePushDevice } from '../lib/pushDevice'

export function useAuth() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const storeLogout = useAuthStore((s) => s.logout)

  // Applies a TokenResponse → session + analytics + cleanup, then lands in chat.
  const completeAuth = (data: TokenResponse, goal?: string, goalParams?: Record<string, unknown>) => {
    acceptAuthenticatedDraft(data.user_id)
    setSession({
      accessToken: data.access_token,
      userId: data.user_id,
      displayName: data.display_name,
    })
    if (goal) metrica.reachGoal(goal, goalParams)
    metrica.setUserId(data.user_id)
    clearInviteToken()
    clearAccessToken()
    // Backfill the Yandex ClientID now that we're authenticated — by login
    // time the Metrica tag has usually initialized, so this catches the value
    // the inline attach missed (bd-ttr). Fire-and-forget, never blocks.
    ensureClientIdBackfilled()
    navigate('/', { replace: true })
  }

  return {
    completeAuth,

    register: async ({ email, displayName, password }: { email: string; displayName?: string; password?: string | null }) => {
      const attempt = beginRegistrationAttempt(getPendingSegment(), useDraftStore.getState().draft)
      const data = await authApi.register({
        segment: attempt.segment,
        email,
        displayName,
        password: password ?? null,
        inviteToken: getInviteToken(),
        accessToken: getAccessToken(),
        yandexClientId: getYandexClientId(),
        timezone: getUserTimeZone(),
      })
      recordCreatedAccount(attempt.id, data.created_user_id)
      // Email verification gate: caller shows the 6-digit code form.
      if ('status' in data && data.status === 'verification_required') {
        metrica.reachGoal('email_code_sent')
        return { needsVerification: true, email: data.email }
      }
      completeAuth(data as TokenResponse, 'register')
      return { needsVerification: false }
    },

    login: async ({ username, password }: { username: string; password: string }) => {
      const data = await authApi.login({ username, password, yandexClientId: getYandexClientId(), timezone: getUserTimeZone() })
      // "Войти" is an existing account: discard any pre-auth onboarding funnel
      // selections a user may have gathered before landing on login, so the
      // onboarding gate doesn't mistake this login for a new-user funnel
      // completion and fire the new-user prompts (notify + install-guide +
      // install-banner) all at once.
      discardGuestDraft()
      completeAuth(data, 'login')
    },

    // «Вход по ключу из почты»: request the one-time code, then log in with
    // it. Same pending-funnel discard as password login — an existing account
    // logging in is not a new-user funnel completion.
    requestLoginCode: async ({ email }: { email: string }) => {
      await authApi.requestLoginCode({ email })
    },

    loginByCode: async ({ email, code }: { email: string; code: string }) => {
      const data = await authApi.loginByCode({ email, code, yandexClientId: getYandexClientId(), timezone: getUserTimeZone() })
      metrica.reachGoal('login_code_success')
      // Same as password login above: an existing account logging in is not
      // a new-user funnel completion — drop pending selections and the
      // guest onboarding draft.
      useOnboardingStore.getState().clearPending()
      discardGuestDraft()
      completeAuth(data, 'login')
    },

    verifyCode: async ({ email, code }: { email: string; code: string }) => {
      const data = await authApi.verifyEmailCode({ email, code, timezone: getUserTimeZone() })
      // Original fired email_verified + register + first_session_start on a
      // successful code verify (onboarding.js:795-803).
      metrica.reachGoal('email_verified')
      completeAuth(data, 'register')
      metrica.reachGoal('first_session_start')
    },

    resetPassword: async ({ token, password }: { token: string; password: string }) => {
      const data = await authApi.resetPassword({ token, password })
      completeAuth(data)
    },

    loginWithGoogleToken: async (idToken: string) => {
      const data = await authApi.google({ idToken, yandexClientId: getYandexClientId(), timezone: getUserTimeZone() })
      completeAuth(data, 'social_login', { provider: 'google' })
    },

    loginWithYandexCode: async (code: string) => {
      const attempt = beginRegistrationAttempt(getPendingSegment(), useDraftStore.getState().draft)
      const data = await authApi.yandex({ segment: attempt.segment, code, yandexClientId: getYandexClientId(), timezone: getUserTimeZone() })
      recordCreatedAccount(attempt.id, data.created_user_id)
      completeAuth(data, 'social_login', { provider: 'yandex' })
    },

    logout: async () => {
      metrica.reachGoal('logout')
      const { userId, accessToken } = useAuthStore.getState()
      if (userId) {
        void disablePushDevice(userId, (subscription) => (
          pushApi.unsubscribeDevice(subscription, accessToken || undefined)
        ), {
          rollbackOnFailure: false,
          awaitBackend: false,
        })
      }
      discardGuestDraft()
      storeLogout()
      // Wipe the previous user's persisted transcript + analytics identity so
      // the next account on this device starts clean (original auth.js:458-459).
      useMessagingStore.getState().clear()
      useMessagingStore.persist.clearStorage()
      useChatwootStore.getState().reset()
      metrica.resetUserId()
      useOnboardingStore.getState().reset() // next user re-checks onboarding
      navigate('/login', { replace: true })
    },
  }
}
